import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { DataFieldValue } from '../../shared/datafields/models/datafield-value';
import { DataField } from '../../shared/datafields/models/datafield';
import { DistributionSearch, DistributionSearchResult } from '../models/distribution-search-result';

import { DocumentEncoding } from '../models/document-encoding';
import { Distribution } from '../models/distribution';
import { FilenamingType } from '../models/filenaming-type';
import { BatchingType } from '../models/batching-type';
import { BatchingFunction } from '../models/batching-function';

import { FileHttpService } from '../../shared/http/file-http.service'
import { JsonHttpService } from '../../shared/http/json-http.service'

import { ItinearyStepParameter } from '../models/itineary-step-parameter';
import { DistributionItineraryConfig, DistributionItineraryConfigSaveObject, DuplicateCheckConfigSaveObject } from '../models/distribution-itinerary-config';
import { BatchingConfiguration } from '../../developer/models/batchconfiguration';
import { DistributionFallbackDirection } from '../models/distribution-fallback-direction';

@Injectable({
    providedIn: 'root'
})
export class DistributionService {
    private httpService = inject(JsonHttpService);
    private fileHttpService = inject(FileHttpService);

    public isFallbackDistribution(distribution: Distribution | null | undefined): boolean {
        if (!distribution) return false;
        return distribution.PartnerIn == null || distribution.PartnerOut == null;
    }

    public fallbackDirection(distribution: Distribution): DistributionFallbackDirection | null {
        if (!distribution) return null;
        if (!this.isFallbackDistribution(distribution)) {
            return null;
        }
        return distribution.PartnerIn == null ? DistributionFallbackDirection.In : DistributionFallbackDirection.Out;
    }

    public isFallbackDistributionSearch(distribution: DistributionSearch): boolean {
        return distribution.FromPartner == null || distribution.ToPartner == null;
    }

    public fallbackDirectionSearch(distribution: DistributionSearch): DistributionFallbackDirection | null {
        if (!this.isFallbackDistributionSearch(distribution)) {
            return null;
        }
        return distribution.FromPartner == null ? DistributionFallbackDirection.In : DistributionFallbackDirection.Out;
    }

    public static readonly maxNumberOfDistributionsToShow = 10;

    public searchDistributions(distributionSearch: any): Promise<DistributionSearchResult> {
        const options = { params: new HttpParams() };
        for (const propertyName in distributionSearch) {
            if (distributionSearch.hasOwnProperty(propertyName)) {
                options.params = options.params.append(propertyName, distributionSearch[propertyName]);
            }
        }
        return this.httpService.get('/api/distribution/search-distributions', options).toPromise();
    }

    public getDistributionCount(): Promise<number> {
        return this.httpService.getTyped<number>('/api/distribution/get-distribution-count').toPromise();
    }

    public async getDistributions(distributionIds: number[], useGenericErrorHandling = true): Promise<Distribution[]> {
        return distributionIds?.length
            ? firstValueFrom(this.httpService.postTyped<Distribution[]>('/api/distribution/get-distributions', distributionIds, undefined, useGenericErrorHandling))
            : []
    }

    public deleteDistribution(distributionId: number): Promise<void> {
        const options = { params: new HttpParams().set('distributionId', `${distributionId}`) };
        return this.httpService.get('/api/distribution/delete-distribution', options).toPromise();
    }

    public saveDistribution(distribution: Distribution, configKeys: DataFieldValue[], itineraryInConfig: DistributionItineraryConfigSaveObject, itineraryOutConfig: DistributionItineraryConfigSaveObject, duplicateCheckConfigs: DuplicateCheckConfigSaveObject[]): Promise<Distribution> {
        const distributionData: any = {
            Distribution: distribution,
            ConfigKeys: configKeys,
            ItineraryInConfig: itineraryInConfig,
            ItineraryOutConfig: itineraryOutConfig,
            DuplicateCheckConfigs: duplicateCheckConfigs
        };
        return this.httpService.post('/api/distribution/save-distribution', distributionData).toPromise();
    }

    public getDocumentEncodings(): Promise<DocumentEncoding[]> {
        return this.httpService.get('/api/distribution/get-document-encodings').toPromise();
    }

    public getFilenamingTypes(distributionObjectId: number): Promise<FilenamingType[]> {
        const options = {
            params: new HttpParams()
                .set('distributionObjectId', `${distributionObjectId}`)
                .set('includeGlobal', 'true')
        };
        return this.httpService.get('/api/distribution/get-filenaming-types', options).toPromise();
    }

    public getBatchingFunctions(): Promise<BatchingFunction[]> {
        return this.httpService.get('/api/distribution/get-batching-functions').toPromise();
    }

    public getBatchingTypes(distributionObjectId: number): Promise<BatchingType[]> {
        const options = {
            params: new HttpParams()
                .set('distributionObjectId', `${distributionObjectId}`)
                .set('includeGlobal', 'true')
        };
        return this.httpService.get('/api/distribution/get-batching-types', options).toPromise();
    }

    public getDistributionConfigurations(keyUsageName: string, distributionId?: number, foreignKey?: number): Promise<DataField[]> {
        const options = { params: new HttpParams().set('keyUsageName', `${keyUsageName}`) };

        if (distributionId) {
            options.params = options.params.append('distributionId', `${distributionId}`);
        }
        if (foreignKey) {
            options.params = options.params.append('dynamicForeignKey', `${foreignKey}`);
        }

        return this.httpService.get('/api/distribution/get-distribution-configurations', options).toPromise();
    }

    setDistributionsIsActive(distributionIds: number[], isActive: boolean): Promise<void> {
        const options = { params: new HttpParams().set('isActive', isActive) };
        return this.httpService.post('/api/distribution/set-distributions-isactive', { Ids: distributionIds }, options).toPromise()
    }

    getItineraryDocumentConfigsForDistribution(documentConfigId: number, distributionId?: number): Promise<DistributionItineraryConfig[]> {
        const options = { params: new HttpParams().set('documentConfigId', `${documentConfigId}`) };

        if (distributionId) {
            options.params = options.params.append('distributionId', `${distributionId}`);
        }

        return this.httpService.get('/api/distribution/get-itinerary-documentconfig-distribution', options).toPromise();
    }

    public getItineraryStepParameters(itineraryStepString: string, distributionId?: number): Promise<ItinearyStepParameter[]> {
        const options = {
            params: new HttpParams()
                .set('itineraryStepString', itineraryStepString)
                .set('distributionId', `${distributionId}`)
        };
        return this.httpService.get('/api/distribution/get-itinerary-step-parameters', options).toPromise();
    }

    // Batching Configuration
    getBatchingConfigurationsByDocumentType(documentTypeId?: number): Promise<BatchingConfiguration[]> {
        const options = {
            params: new HttpParams()
                .set('documentTypeId', `${documentTypeId}`)
        };
        return this.httpService.get('/api/developer/batchingconfigurations/get-batchingconfigurations-by-documenttype', options).toPromise();
    }

    /**
     * Uploads the given file using the distribution with the given distribution-id
     * @param distributionId Id for the distribution to use
     * @param testFile The file to upload
     * @return Promise with the id of the generated interchange-guid
     */
    public uploadTestFile(distributionId: number, testFile: File): Promise<number> {
        const options = { params: new HttpParams().set('distributionId', `${distributionId}`) };
        return new Promise<number>((resolve, reject) => {
            this.fileHttpService.upload<any>('/api/distribution/test-file/upload', [testFile], options).then((result) => {
                resolve(result.InterchangeId);
            }).catch(reject);
        });
    }
}
